import * as Sentry from "@sentry/react";

import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import _findIndex from "lodash/findIndex";
import _isEqual from "lodash/isEqual";
import { AppThunk } from "store/store";
import { useMapStore } from "stores/map";
import { useMapViewFollowUpStore } from "stores/mapViewFollowUpStore";
import { uid } from "utils";

import {
  generateFilterQuery,
  getFilterCountsFromFilterId,
  getFilterMetadata
} from "api/filter";
import { MapExtent } from "api/useTownshipRangeGrid";

import { LegendItemModel } from "models/LegendItem";
import { EntityKind } from "models/entityKind";
import {
  ActiveFilterItem,
  FilterModel,
  IFilterState,
  IPropertyLegendFilter
} from "models/filter";

import toLegendItem from "components/filter/toLegendItem";
import {
  combineFiltersWithPriority,
  getLegendFilter,
  getOthersRollupFilter
} from "components/filter/util";

import { EMPTY_FILTER_ID } from "../../../constants/app.constants";
import {
  FilterState,
  SortByT,
  setIsMidstreamNavigatorFilter as setMainFilterMidstreamNavigatorFilter
} from "./filterSlice";
import hasOnlyMapPolygonFilterChanges from "./utils/hasOnlyMapPolygonFilterChanges";
import { hasWellListFilter } from "./utils/hasWellListFilter";
import { polygonCoordsEqual } from "./utils/polygonCoordsEqual";

const initialState: FilterState = {
  filterId: {
    id: "",
    lastUpdated: 0
  },
  sortBy: "AlphabeticalAsc",
  showGroupsNotInFilter: true,
  filterSource: "extent",
  wellListFilter: [],
  polygonFilter: null,
  excludePolygonsFilter: [],
  isFiltering: false,
  wellListFilterExtent: undefined,
  resetPolygonFilter: false,
  updatePlayLegend: "",
  currentWellList: [],
  filterCounts: {},
  propertiesFilter: [],
  currentFilter: undefined, //global currently applied filter
  selectedSavedFilter: ActiveFilterItem,
  filterHistoryJSON: [],
  savedFilters: [],
  filtersLoaded: false,
  currentPlayList: [],
  isMidstreamNavigatorFilter: false,
  skipMapViewOnNextFilter: false,
  mapViewFilterDisabled: false
};

const facilityFilterSlice = createSlice({
  name: "facilityFilter",
  initialState,
  reducers: {
    updateCurrentFilter(state, action: PayloadAction<FilterModel>) {
      if (action.payload && !action.payload.query.predicates.includes(null)) {
        state.currentFilter = action.payload;
      }
    },
    setFilterCounts(state, action) {
      state.filterCounts = action.payload;
    },
    setFilterState(state, action: PayloadAction<IFilterState>) {
      const filters = action.payload;
      if (!filters) return;
      state.wellListFilter =
        action.payload.wellListFilter && !action.payload.wellListFilter.includes(null)
          ? action.payload.wellListFilter
          : [];
      if (!action.payload.polygonFilter) {
        //no polygon filter saved (i.e. no selection)
        state.resetPolygonFilter = true; // reset filter
        if (state.wellListFilter.length > 0) {
          // A stored list with no viewport of its own — a saved filter
          // restore. It has to come back whole, so the run that follows leaves
          // the map view out. A payload carrying its own polygon (session
          // snapshot, undo) is reproducing a view and keeps it.
          state.skipMapViewOnNextFilter = true;
        }
      } else {
        state.polygonFilter = action.payload.polygonFilter;
      }

      // Any wholesale replacement of the filter re-enables the map view.
      state.mapViewFilterDisabled = false;
      state.excludePolygonsFilter = action.payload.excludePolygonsFilter ?? [];
      state.propertiesFilter = action.payload.propertiesFilter;
      state.isMidstreamNavigatorFilter = false;
    },
    setFilterId(state, action: PayloadAction<{ id: string; lastUpdated: number }>) {
      const filterId = action.payload;
      // If filterId is null,undefined or empty, don't set filter id
      if (!filterId) return;
      state.filterId = filterId;
      state.filterSource = "well-list";
    },
    setCurrentWellList(state, action) {
      state.currentWellList = action.payload;
    },
    setResetPolygonFilter(state, action) {
      state.resetPolygonFilter = action.payload;
    },
    setUpdatePlayLegend(state, action) {
      state.updatePlayLegend = action.payload;
    },
    setSavedFilters(state, action) {
      state.savedFilters = action.payload;
    },
    removeAllProperties(state) {
      state.propertiesFilter = [];
      state.wellListFilter = [];
      state.excludePolygonsFilter = [];
      state.updatePlayLegend = "";
      state.isMidstreamNavigatorFilter = false;
      // The list this was holding whole is gone, so the viewport is back.
      state.mapViewFilterDisabled = false;
    },
    removeProperty(state, action) {
      const pred = JSON.parse(JSON.stringify(action.payload));
      const properties = JSON.parse(JSON.stringify(state.propertiesFilter));
      const found = properties.filter((p) =>
        p.predicates.find((prop) => {
          return _isEqual(prop, pred);
        })
      );

      if (found.length === 0) {
        return;
      }
      const idx = _findIndex(found[0].predicates, (p) => {
        return _isEqual(p, pred);
      });
      //if predicate is not found then return
      if (idx < 0) {
        return;
      }
      found[0].predicates.splice(idx, 1);
      if (found[0].predicates.length === 0) {
        found.forEach((item) => {
          const index = _findIndex(properties, (p) => _isEqual(p, item));
          if (index > -1) {
            properties.splice(index, 1);
          }
        });
      }
      if (pred.property === "Header.ResourcePlay") {
        state.updatePlayLegend = "";
      }
      state.propertiesFilter = properties;
    },
    filterWellsFromList(
      state,
      action: PayloadAction<{ wellList: string[]; resetMidstreamFlag?: boolean }>
    ) {
      const { wellList, resetMidstreamFlag = true } = action.payload;
      const wellListFilter = {
        type: "WellList",
        property: "UniqueId",
        operator: "in",
        id: uid(),
        filterType: "well-list",
        value: wellList
      };
      state.wellListFilter = [wellListFilter];
      state.propertiesFilter = [];
      state.resetPolygonFilter = true; // reset polygon filter
      state.excludePolygonsFilter = [];
      // The list the user just chose is a set of facilities, not a statement
      // about the viewport, so it applies whole; the map view starts narrowing
      // again on the run after this one.
      state.skipMapViewOnNextFilter = true;
      state.mapViewFilterDisabled = false;

      // Only reset flag if requested (defaults to true for external operations)
      if (resetMidstreamFlag) {
        state.isMidstreamNavigatorFilter = false;
      }
    },

    setSkipMapViewOnNextFilter(state, action: PayloadAction<boolean>) {
      state.skipMapViewOnNextFilter = action.payload;
    },
    setMapViewFilterDisabled(state, action: PayloadAction<boolean>) {
      state.mapViewFilterDisabled = action.payload;
    },

    setIsFiltering(state, action: PayloadAction<boolean>) {
      state.isFiltering = action.payload;
    },
    ignoreWellsFromWellList(state, wellListPayload) {
      let wellListFilter = null;
      const wellList = wellListPayload.payload;
      if (wellList.length > 0) {
        wellListFilter = {
          type: "WellList",
          property: "UniqueId",
          operator: "nin",
          id: uid(),
          filterType: "well-list",
          value: wellList
        };
        state.wellListFilter.push(wellListFilter);
      }
    },
    removeWellListFilter(state, action) {
      const payload = action.payload;
      const index = state.wellListFilter.findIndex((filter) => filter.id === payload.id);
      if (index < 0) {
        return;
      }
      state.isMidstreamNavigatorFilter = false;
      state.wellListFilter.splice(index, 1);
      // The viewport was only turned off to hold that list whole. With the
      // list gone there is nothing to hold, so the map view comes back.
      state.mapViewFilterDisabled = false;
    },
    filterWellsFromMapExtent(state, polygonPayload) {
      const polygon = polygonPayload.payload;
      // The map view was removed from the filter, so a pan must not put it
      // back — whoever dispatched this, not only the filter watcher.
      if (state.mapViewFilterDisabled && !polygon?.source) return;
      // Skip update if coordinates haven't changed (prevents redundant doFilter on pan)
      if (polygonCoordsEqual(state.polygonFilter, polygon)) return;
      // A polygon the user drew is not a pan: it says which facilities to
      // keep, so it lands, and the viewport is on again behind it. Turned on
      // only by an update that lands — a lasso the reducer skipped above
      // changed nothing, and the list is still being held whole.
      state.mapViewFilterDisabled = false;
      //make a copy in case polygon gets cleared
      const json = JSON.stringify(polygon);
      const poly = JSON.parse(json);
      poly.id = uid();
      state.polygonFilter = poly;
    },
    excludeWellsInPolygon(state, polygonPayload) {
      const polygon = polygonPayload.payload;
      //make a copy in case polygon gets cleared
      const json = JSON.stringify(polygon);
      const poly = JSON.parse(json);
      poly.id = uid();
      state.excludePolygonsFilter.push(poly);
    },
    removeExcludePolygon(state, action) {
      const pred = JSON.parse(JSON.stringify(action.payload));
      const polygons = JSON.parse(JSON.stringify(state.excludePolygonsFilter));
      const remaining = polygons.filter((p) => p.id !== pred.id);

      // Nothing removed
      if (remaining.length === polygons.length) {
        return;
      }

      state.excludePolygonsFilter = remaining;
    },
    removeAllExcludePolygon(state) {
      state.excludePolygonsFilter = [];
    },
    setFilterHistory(state, payload: PayloadAction<string[]>) {
      state.filterHistoryJSON = [...payload.payload];
    },
    addToFilterHistory(state, payload: PayloadAction<IFilterState>) {
      if (!hasOnlyMapPolygonFilterChanges(state.filterHistoryJSON, payload.payload)) {
        state.filterHistoryJSON = [
          ...state.filterHistoryJSON,
          JSON.stringify(Object.assign({}, payload.payload))
        ].slice(-200);
      }
    },
    addManyPropertiesToFilter(
      state,
      filterPayload: PayloadAction<IPropertyLegendFilter>
    ) {
      const properties = filterPayload.payload.legendFilters;
      const propertyName = filterPayload.payload.propertyName;
      if (!propertyName) {
        return;
      }
      const props = [...state.propertiesFilter];
      const found = props.filter((p) =>
        p.predicates.find((prop) => prop.property === propertyName)
      );
      found.forEach((item) => {
        const index = props.indexOf(item);
        if (index > -1) {
          props.splice(index, 1);
        }
      });
      if (properties && properties.length > 0) {
        for (const property of properties) {
          for (const pred of property.predicates) {
            pred.id = uid();
          }
          props.push(property);
        }
      }
      state.propertiesFilter = props;
      state.isMidstreamNavigatorFilter = false;
    },
    updateSortBy(state, action: PayloadAction<SortByT>) {
      state.sortBy = action.payload;
    },
    updateShowGroupsNotInFilter(state, action: PayloadAction<boolean>) {
      state.showGroupsNotInFilter = action.payload;
    },
    toggleShowGroupsNotInFilter(state) {
      state.showGroupsNotInFilter = !state.showGroupsNotInFilter;
    },
    setSelectedSavedFilter(state, action) {
      state.selectedSavedFilter = action.payload;
    },

    setWellListExtent(state, action: PayloadAction<MapExtent>) {
      state.wellListFilterExtent = action.payload;
    },
    setFiltersLoaded(state, action: PayloadAction<boolean>) {
      state.filtersLoaded = action.payload;
    },
    setIsMidstreamNavigatorFilterReducer(state, action: PayloadAction<boolean>) {
      state.isMidstreamNavigatorFilter = action.payload;
    },
    resetFacilityFilterSlice: () => initialState
  }
});

export const setIsMidstreamNavigatorFilter =
  (value: boolean): AppThunk =>
  (dispatch) => {
    // Set the local flag
    dispatch(setIsMidstreamNavigatorFilterReducer(value));

    // If setting to false, also clear the main filter slice flag to maintain synchronization
    if (!value) {
      dispatch(setMainFilterMidstreamNavigatorFilter(false));
    }
  };

export const updateActiveFilterCount =
  (filterId: string): AppThunk =>
  async (dispatch) => {
    if (!filterId || filterId === EMPTY_FILTER_ID) {
      // If filter is null,undefined or empty, don't get filter count
      return;
    }
    const filterCountsResponse = await getFilterCountsFromFilterId(filterId);

    if (filterCountsResponse.status === 200) {
      const filterCounts = filterCountsResponse.data;
      dispatch(setFilterCounts(filterCounts));
    }
  };

export const undoFilter = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const history = [...state.facilityFilter.filterHistoryJSON];
  const mapCoordinates = useMapStore.getState().mapExtent.coordinates;

  if (history.length > 1) {
    const item = history[history.length - 2];
    dispatch(setFilterHistory(history.slice(0, -1)));
    const filterState: IFilterState = JSON.parse(item);
    // A history entry can hold no polygon at all: a run that left the map view
    // out writes the filter without one, so stepping back to it has nothing to
    // read a source from and nothing to put today's viewport into.
    const restoresMapView = filterState.polygonFilter?.source !== "selection";
    if (restoresMapView && filterState.polygonFilter) {
      filterState.polygonFilter.coordinates = mapCoordinates;
    }
    await dispatch(setFilterState(filterState));
    if (restoresMapView && filterState.wellListFilter?.length > 0) {
      // The polygon coming back is today's viewport, not the one this entry
      // was filtered in. Stepping back to a list has to give the list back
      // whole, the same as applying one does, or an undo hands back fewer
      // facilities than it took away.
      dispatch(setSkipMapViewOnNextFilter(true));
    }
  }
};
export const setFilterToSystemDefault = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const facilityDefaultFilter = state.facilityFilter.savedFilters.find(
    (filter) => filter.id === "FacilityDefault"
  );

  if (facilityDefaultFilter) {
    try {
      // Parse the filterJSON to get the actual filter data
      const filterData = JSON.parse(facilityDefaultFilter.filterJSON);

      // Create filter state with the default properties filter
      const defaultFilterState: IFilterState = {
        wellListFilter: [],
        polygonFilter: null,
        excludePolygonsFilter: [],
        propertiesFilter: filterData.propertiesFilter || []
      };

      await dispatch(setFilterState(defaultFilterState));
    } catch (error) {
      // If JSON parsing fails, set empty filter state
      const emptyFilterState: IFilterState = {
        wellListFilter: [],
        polygonFilter: null,
        excludePolygonsFilter: [],
        propertiesFilter: []
      };

      await dispatch(setFilterState(emptyFilterState));
    }
  }
};
export const addLegendItemToFilter =
  (title: string, groupByProperty?: Maybe<string>): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const legendItems = useMapStore.getState().txnId?.legend?.legendItems as
      | LegendItemModel[]
      | undefined;
    if (!legendItems) {
      return;
    }
    const items: LegendItemModel[] = legendItems.filter(
      (li: LegendItemModel) => li.groupTitle === title || li.title === title
    );
    if (items.length === 0) {
      return;
    }
    const item = toLegendItem(
      items,
      state.groupBy?.globalGroupBy,
      state.filter.propertiesFilter
    )[0];
    const filter = getLegendFilter(
      item.property,
      item.canBin,
      [item].map((item) => item.title),
      item.categoryId,
      item.bin,
      item.normalizeBySettings,
      item.isNotEqual,
      item.pdenSource,
      item.value,
      item.groupByTitle,
      item.partialContextTitle
    );
    dispatch(
      addManyPropertiesToFilter({
        legendFilters: [filter],
        propertyName: groupByProperty ?? state.groupBy.globalGroupBy?.property
      })
    );
  };

export const addMultipleLegendItemsToFilter =
  (titles: string[], isExclude: boolean, groupByProperty?: Maybe<string>): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const legendItems = useMapStore.getState().txnId?.facilityLegend?.legendItems as
      | LegendItemModel[]
      | undefined;
    const currentFilter = state.facilityFilter.propertiesFilter;
    if (!legendItems) {
      return;
    }
    const items: LegendItemModel[] = legendItems
      .filter(
        (li: LegendItemModel) =>
          titles.includes(li.groupTitle) || titles.includes(li.title)
      )
      .map((item: LegendItemModel) => {
        return { ...item, isNotEqual: true };
      });
    if (items.length === 0) {
      return;
    }
    const newLegendItems = toLegendItem(
      items,
      state.groupBy?.globalFacilityFocus,
      state.facilityFilter.propertiesFilter
    );
    // Others can only be excluded, never included.
    const lassoItems = newLegendItems.legendItems.filter(
      (item) => isExclude || !item.isOthersRollup
    );
    if (lassoItems.length === 0) {
      return;
    }
    const legendFilters = lassoItems.map((item) => {
      if (item.isOthersRollup) {
        return getOthersRollupFilter(
          item.property,
          item.categoryId,
          item.bin,
          isExclude,
          item.pdenSource,
          item.groupByTitle,
          item.partialContextTitle,
          item.forecastTrumpingSettings,
          item.performanceDateRange
        );
      }
      return getLegendFilter(
        item.property,
        item.canBin,
        [item].map((item) => item.title),
        item.categoryId,
        item.bin,
        item.normalizeBySettings,
        isExclude,
        item.pdenSource,
        item.value,
        item.groupByTitle,
        item.partialContextTitle
      );
    });
    const combinedFilters = [
      ...combineFiltersWithPriority(currentFilter, legendFilters, legendItems)
    ];
    dispatch(
      addManyPropertiesToFilter({
        legendFilters: isExclude ? combinedFilters : legendFilters,
        propertyName: groupByProperty ?? state.groupBy.globalGroupBy?.property
      })
    );
  };

// Content hash of the last-applied well list, used only to skip redundant
// map-extent updates when reapplying a filter yields the same wells. Thunk-
// local memoization, not app state — doesn't belong in Redux (no new Redux
// state per state-management.instructions.md) and nothing else reads it.
let previousFacilityWellListContentHash = "";

export const doFilter =
  (getFilter, zoomToFilterEnabled = false, isUserFilterChange = true): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();

    dispatch(setIsFiltering(true));
    // See filterSlice.doFilter (EVA-69).
    const currentFilterId = state.facilityFilter.filterId.id;
    const previousFilterId = currentFilterId === EMPTY_FILTER_ID ? "" : currentFilterId;
    const polygonFilter = state.facilityFilter.polygonFilter;
    const hasWellList = hasWellListFilter(state.facilityFilter?.wellListFilter);
    // Removing the map-view node only means anything while a facility list is
    // carrying the result. With no list the viewport is the whole filter, so
    // it keeps working the way it always has.
    const mapViewFilterDisabled =
      state.facilityFilter.mapViewFilterDisabled && hasWellList;
    // The map view is the one polygon nobody marks. A polygon that says where
    // it came from is the user's own, and leaving it out would quietly widen
    // the result instead of holding a list whole.
    const isMapViewPolygon = !polygonFilter?.source;
    // No viewport yet means no map to filter against — unless the map view was
    // removed from the filter, in which case there is nothing to wait for.
    if (!polygonFilter?.type?.length && !mapViewFilterDisabled) {
      dispatch(setIsFiltering(false));
      return;
    }
    // Read after the guard above, never beside getState(): a run that bailed
    // out issued no request, so spending the flag there would narrow the first
    // real run after the map mounts.
    const skipMapView = state.facilityFilter.skipMapViewOnNextFilter;
    // Leaving the map view out is only ever about holding a list whole. With
    // no list there is nothing to hold, and dropping the viewport would filter
    // against nothing at all.
    const omitMapView =
      hasWellList && isMapViewPolygon && (skipMapView || mapViewFilterDisabled);
    // Spent only by the run that used it, and only once that run has landed.
    // A newer run reads this state and builds its query before it cancels the
    // one in flight, so spending the flag up front would hand the replacement
    // a query with the viewport in it and narrow the list this flag was
    // holding whole. A run that kept the map view anyway — a lasso is on file,
    // or there is no list — never took its turn either.
    const skipMapViewUsed = skipMapView && omitMapView;
    // A run held the map view out for one turn owes it a turn of its own: the
    // two runs read the same state apart from the flag, so nothing else can
    // tell that the viewport never got its say.
    const owesMapViewFollowUp = omitMapView && !mapViewFilterDisabled;
    const query = generateFilterQuery(
      state.facilityFilter.wellListFilter,
      omitMapView ? null : state.facilityFilter.polygonFilter,
      state.facilityFilter.excludePolygonsFilter,
      state.facilityFilter.propertiesFilter,
      previousFilterId,
      null,
      EntityKind.Facility,
      // This is the live filter, so an unmarked polygon here really is the map
      // the user is looking at.
      true
    );
    query.entityKind = EntityKind.Facility;
    const currentFilterState: IFilterState = {
      polygonFilter: state.facilityFilter.polygonFilter,
      excludePolygonsFilter: state.facilityFilter.excludePolygonsFilter,
      wellListFilter: state.facilityFilter.wellListFilter,
      propertiesFilter: state.facilityFilter.propertiesFilter
    };
    dispatch(addToFilterHistory(currentFilterState));
    const result = await getFilter(query);

    let filterId = "";
    if (result && result.ok) {
      filterId = result.value;
    } else {
      return;
    }
    // The list has been filtered whole, so the one-shot is spent.
    if (skipMapViewUsed) {
      dispatch(setSkipMapViewOnNextFilter(false));
    }
    dispatch(updateCurrentFilter(query));
    dispatch(setFilterId({ id: filterId, lastUpdated: Date.now() }));
    if (hasWellList || zoomToFilterEnabled) {
      try {
        const metadataResponse = await getFilterMetadata(filterId);
        if (metadataResponse?.status === 200) {
          const newHash = metadataResponse.data.contentHash;
          // Two ways to earn a new zoom extent, and no others.
          //
          // The first is the same result set with no extent to show it in.
          // This hash is a module-level `let` that survives
          // resetFacilityFilterSlice() (logout is an SPA route change, no
          // reload), so after logout the Redux extent is undefined while the
          // hash still holds the old value — the extent has to be restored
          // even though nothing about the result changed.
          //
          // The second is a result set the user asked for and actually got: a
          // different hash, from a filter change of their own. A refilter the
          // viewport itself caused must not move the viewport, or panning
          // refits the camera, which pans again, and the result keeps
          // shrinking. That also keeps the extent on the run carrying the
          // whole list rather than the narrowed run that follows it.
          const extentUnset = state.facilityFilter.wellListFilterExtent === undefined;
          const hashChanged = previousFacilityWellListContentHash !== newHash;
          // Recorded by every run that came back with a result, not only by
          // the ones that earned an extent. A run that skipped the extent
          // still saw this result set, so a later run returning the same one
          // has changed nothing and must not read as a change.
          previousFacilityWellListContentHash = newHash;
          if ((extentUnset && !hashChanged) || (isUserFilterChange && hashChanged)) {
            dispatch(setWellListExtent(metadataResponse.data.boundingBox));
          }
        } else {
          // Non-200 (3xx / other 2xx): surface it instead of silently keeping a
          // stale extent, mirroring the throw-on-non-200 handling in filterSlice.
          throw new Error(`getFilterMetadata returned ${metadataResponse?.status}`);
        }
      } catch (err) {
        // Network failure — don't block the filter flow, but still report it.
        Sentry.withScope((scope) => {
          scope.setContext("facilityFilter", { filterId, action: "getFilterMetadata" });
          scope.setLevel("warning");
          Sentry.captureException(err);
        });
      }
    } else {
      previousFacilityWellListContentHash = "";
      dispatch(setWellListExtent(undefined));
    }

    // A run that came back settles the debt outright: it either left the map
    // view out and owes it a turn, or it carried the map view and nothing is
    // outstanding — including anything an earlier filter left behind.
    useMapViewFollowUpStore
      .getState()
      .setMapViewFollowUpOwed(EntityKind.Facility, owesMapViewFollowUp);
    dispatch(setIsFiltering(false));
    return filterId;
  };

// Stores a zoom extent for the already-applied facility filter, keeping the
// content hash current so the next doFilter still skips redundant extent
// updates for an unchanged result set. Used by the zoomToFilterResults
// coordinator when the zoom-to-filter toggle is switched on.
export const applyZoomToFilterExtent =
  (extent: MapExtent, contentHash: string): AppThunk =>
  (dispatch) => {
    previousFacilityWellListContentHash = contentHash;
    dispatch(setWellListExtent(extent));
  };

export const clearFacilityFilter = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  if (state.facilityFilter.filterId.id === EMPTY_FILTER_ID) return;
  dispatch(setFilterId({ id: EMPTY_FILTER_ID, lastUpdated: Date.now() }));
};

export const {
  updateCurrentFilter,
  setFilterId,
  setFilterCounts,
  updateSortBy,
  updateShowGroupsNotInFilter,
  toggleShowGroupsNotInFilter,
  setFilterState,
  setCurrentWellList,
  setFilterHistory,
  setResetPolygonFilter,
  setUpdatePlayLegend,
  removeProperty,
  filterWellsFromList,
  setSkipMapViewOnNextFilter,
  setMapViewFilterDisabled,
  ignoreWellsFromWellList,
  removeAllProperties,

  removeWellListFilter,
  setWellListExtent,
  filterWellsFromMapExtent,
  excludeWellsInPolygon,
  setIsFiltering,
  removeExcludePolygon,
  removeAllExcludePolygon,
  addManyPropertiesToFilter,
  setSelectedSavedFilter,
  setSavedFilters,
  addToFilterHistory,
  setFiltersLoaded,
  setIsMidstreamNavigatorFilterReducer,
  resetFacilityFilterSlice
} = facilityFilterSlice.actions;

export default facilityFilterSlice.reducer;
